import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import SearchInput from "../../../components/SearchInput/SearchInput";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import style from "../Style.module.scss";
import pageHeadingStyles from "../../../styles/PageHeader.module.scss";
import Filter from "../../../assets/svg/icons/common/Filter";
import RedDotBadge from "../../../assets/svg/icons/common/RedDotBadge";
import CloseIconWhite from "../../../assets/svg/icons/common/CloseIconWhite";
import Listing from "./List/Listing";
import FilterDrawer from "./FilterDrawer/Index";
import { getPreapp } from "../../../../api";
import { toast, Toaster, ToastBar } from "react-hot-toast";
import useMerchantStore from "../../../stores/merchant";
import useCommonStore from "../../../stores/common";

const isRangeModified = (from, to, min, max) => {
  if (from == null || to == null) return false;
  return Number(from) > min || Number(to) < max;
};

function getMerchantFilterCount(query, bounds) {
  if (!query) return 0;
  const mf = query.merchant_filter || {};
  const hasDateRange = !!(mf.date_approved_from || mf.date_approved_to);
  const hasVolumeCap = isRangeModified(
    mf.volume_from,
    mf.volume_to,
    bounds.volume_cap.min,
    bounds.volume_cap.max,
  );
  const hasVolumeUsed = isRangeModified(
    mf.volume_used_from,
    mf.volume_used_to,
    bounds.volume_used.min,
    bounds.volume_used.max,
  );
  const hasVolumeMTD = isRangeModified(
    mf.volume_mtd_from,
    mf.volume_mtd_to,
    bounds.volume_mtd.min,
    bounds.volume_mtd.max,
  );
  const hasMomChange = isRangeModified(
    mf.mom_change_from,
    mf.mom_change_to,
    bounds.mom_change.min,
    bounds.mom_change.max,
  );
  return (
    (mf.org_filter ? 1 : 0) +
    (mf.status && mf.status !== "active" ? 1 : 0) +
    (hasDateRange ? 1 : 0) +
    (hasVolumeCap ? 1 : 0) +
    (hasVolumeUsed ? 1 : 0) +
    (hasVolumeMTD ? 1 : 0) +
    (hasMomChange ? 1 : 0) +
    (query.ems_office?.length ?? 0) +
    (query.ems_branch?.length ?? 0) +
    (query.rep_codes?.length ?? 0)
  );
}

function Merchants() {
  const setMerchantList = useMerchantStore((state) => state.setMerchantList);
  const setMerchantListLoading = useMerchantStore(
    (state) => state.setMerchantListLoading,
  );
  const setPageCount = useMerchantStore((state) => state.setPageCount);
  const setTotalItems = useMerchantStore((state) => state.setTotalItems);
  const setItemsPerPage = useMerchantStore((state) => state.setItemsPerPage);
  const showDetails = useCommonStore((state) => state.showDetails);
  const filterBounds = useMerchantStore((state) => state.filterBounds);
  const filterBoundsLoading = useMerchantStore(
    (state) => state.filterBoundsLoading,
  );
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const merchantFilterSearchQuery = useMerchantStore(
    (state) => state.merchantFilterSearchQuery,
  );
  const setMerchantFilterSearchQuery = useMerchantStore(
    (state) => state.setMerchantFilterSearchQuery,
  );

  const [searchText, setSearchText] = useState("");
  const [filterApplied, setFilterApplied] = useState(false);
  const cancelRef = useRef(null);
  const ignoreRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const onChangeSearchInput = (e) => {
    setSearchText(e.target.value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setMerchantFilterSearchQuery({
        search: e.target.value,
        page: 1,
      });
    }, 500);
  };

  const loadPreapps = useCallback(() => {
    if (cancelRef.current) {
      cancelRef.current.cancel();
    }

    cancelRef.current = axios.CancelToken.source();
    ignoreRef.current = false;

    setMerchantListLoading(true);

    getPreapp(merchantFilterSearchQuery, cancelRef.current.token)
      .then((res) => {
        if (ignoreRef.current) {
          return;
        }

        if (res.status) {
          setMerchantListLoading(false);
          setTotalItems(res?.total_count);
          setMerchantList(res?.data?.data);
          setPageCount(res?.data?.last_page);
          setItemsPerPage(50);
        }
      })
      .catch((error) => {
        if (ignoreRef.current) {
          return;
        }

        if (axios.isCancel(error)) {
          console.log("Request cancelled");
          return;
        }

        setMerchantListLoading(false);
        console.error("Error loading merchants:", error);
      });
  }, [
    merchantFilterSearchQuery,
    setMerchantListLoading,
    setTotalItems,
    setMerchantList,
    setPageCount,
    setItemsPerPage,
  ]);

  useEffect(() => {
    loadPreapps();
    setFilterApplied(
      getMerchantFilterCount(merchantFilterSearchQuery, filterBounds) > 0,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on individual filter fields
  }, [
    merchantFilterSearchQuery.search,
    merchantFilterSearchQuery.merchant_filter,
    merchantFilterSearchQuery.ems_office,
    merchantFilterSearchQuery.ems_branch,
    merchantFilterSearchQuery.rep_codes,
    merchantFilterSearchQuery.sortBy,
    merchantFilterSearchQuery.sortValue,
    merchantFilterSearchQuery.page,
    loadPreapps,
  ]);

  useEffect(() => {
    return () => {
      ignoreRef.current = true;
      if (cancelRef.current) {
        console.log("before cancel");
        cancelRef.current.cancel();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const filterCount = getMerchantFilterCount(
    merchantFilterSearchQuery,
    filterBounds,
  );

  return (
    <>
      <div className={style.mobile_header}>
        <div className={style.mobile_filters}>
          <div className={style.search_wrapper}>
            <SearchInput onChange={onChangeSearchInput} value={searchText} />
          </div>
          <ButtonIcon
            bgColor="#EDEDF4"
            onClick={() => setFilterDrawerOpen(true)}
            className={style.filter_button}
          >
            <Filter />
            {filterApplied && <RedDotBadge className={style.filter_active} />}
          </ButtonIcon>
        </div>
      </div>

      <div
        className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column}`}
      >
        <div className={pageHeadingStyles.title_contents}>
          <p>All merchants</p>
        </div>

        <div className={style.search_input_container}>
          <SearchInput onChange={onChangeSearchInput} value={searchText} />

          {!filterBoundsLoading && (
            <ButtonIcon
              bgColor="#EDEDF4"
              onClick={() => setFilterDrawerOpen(true)}
              className={style.filter_button}
            >
              <Filter />
              {filterApplied && <RedDotBadge />}
              Filter
              {filterApplied && filterCount > 0 ? `: ${filterCount}` : ""}
            </ButtonIcon>
          )}

          {!showDetails && (
            <>
              {/* <ButtonIcon
                bgColor="#EDEDF4"
                onClick={() => setFilterDrawerOpen(true)}
              >
                <Download />
                <span>Download</span>
              </ButtonIcon> */}
              {/* <ButtonIcon
                bgColor="#4C70EF"
                textColor="#fff"
                onClick={() => setFilterDrawerOpen(true)}
              >
                <Plus />
                <span>Add Merchant</span>
              </ButtonIcon> */}
            </>
          )}
        </div>
      </div>

      <div className={style.card}>
        <Listing />
      </div>

      <FilterDrawer />

      {/* Toaster global component */}
      <Toaster
        position="top-right"
        containerStyle={{}}
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  className={style.toast_dismissal_button}
                  onClick={() => toast.dismiss(t.id)}
                >
                  <CloseIconWhite />
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </>
  );
}

export default Merchants;
